import React, { useState, useEffect, useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  bookingService,
  buildSelectedVehiclePayloadContext,
  buildWebsiteEnquiryPayload,
  getAirportRateCardTripType,
  normalizeBookingType,
  toVehicleBackendId,
} from '../../services/bookingService';
import {
  fetchPublicVehicles,
  groupVehiclesByTrip,
} from '../../services/vehicleService';
import LocationAutocompleteInput from './LocationAutocompleteInput';

const normalizeMatchText = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getVehicleMatchText = (vehicle = {}) =>
  normalizeMatchText(
    [
      vehicle.id,
      vehicle.name,
      vehicle.models,
      vehicle.type,
      vehicle.category,
      vehicle.vehicleType,
      vehicle.vehicleCategory,
      vehicle.image,
      vehicle.catalogKey,
    ]
      .filter(Boolean)
      .join(' ')
  );

const getTripKey = (bookingType = '') => {
  const normalizedBookingType = normalizeBookingType(bookingType);

  if (normalizedBookingType === 'Outstation') return 'outstation';
  if (normalizedBookingType === 'Local Taxi') return 'local';
  return 'airport';
};

const normalizeApiText = (value = '') =>
  normalizeMatchText(value).toUpperCase();

const EXPECTED_VEHICLE_SPECS = [
  {
    keywords: ['tempo traveller 12+1', 'tempo12', 'tempo 12'],
    vehicleType: 'TEMPO TRAVELLER 12+1',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['tempo traveller 13+1', 'tempo13', 'tempo 13'],
    vehicleType: 'TEMPO TRAVELLER 13+1',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['tempo traveller 7/9/12', 'tempo traveller 7 9 12', 'tempo luxury'],
    vehicleType: 'TEMPO TRAVELLER 7/9/12',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['urbania 9+1', 'urbania9pkn'],
    vehicleType: 'URBANIA 9+1',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['innova crysta 6+1', 'innova6'],
    vehicleType: 'INNOVA CRYSTA 6+1',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['innova crysta 7+1', 'innova7', 'crysta'],
    vehicleType: 'INNOVA CRYSTA 7+1',
    vehicleCategory: 'SUPER LUXURY',
  },
  {
    keywords: ['innova hybrid'],
    vehicleType: 'INNOVA HYBRID 7+1',
    vehicleCategory: 'LUXURY PLUS',
  },
  {
    keywords: ['hycross hybrid', 'hycross'],
    vehicleType: 'HYCROSS HYBRID 7+1',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['fortuner'],
    vehicleType: 'FORTUNER 7+1',
    vehicleCategory: 'SUPER LUXURY',
  },
  {
    keywords: ['ertiga', 'rumion'],
    vehicleType: 'ERTIGA 6+1',
    vehicleCategory: 'SUPER LUXURY',
  },
  {
    keywords: ['etios'],
    vehicleType: 'ETIOS 4+1',
    vehicleCategory: 'COMFORT',
  },
  {
    keywords: ['sedan ac', 'sedan', 'dzire', 'ciaz', 'amaze'],
    vehicleType: 'SEDAN AC 4+1',
    vehicleCategory: 'COMFORT',
  },
  {
    keywords: ['tempo traveller', 'tempo', 'traveller'],
    vehicleType: 'TEMPO TRAVELLER',
    vehicleCategory: 'PREMIUM',
  },
  {
    keywords: ['urbania'],
    vehicleType: 'URBANIA',
    vehicleCategory: 'LUXURY',
  },
  {
    keywords: ['bus'],
    vehicleType: 'BUS',
    vehicleCategory: 'LUXURY',
  },
];

const BOOKING_VEHICLE_OPTIONS = [
  { value: 'sedan-ac-4+1-comfort', name: 'SEDAN AC 4+1', category: 'COMFORT' },
  { value: 'etios-4+1-comfort', name: 'ETIOS 4+1', category: 'COMFORT' },
  { value: 'ertiga-6+1-super-luxury', name: 'ERTIGA 6+1', category: 'SUPER LUXURY' },
  { value: 'innova-crysta-7+1-super-luxury', name: 'INNOVA CRYSTA 7+1', category: 'SUPER LUXURY' },
  { value: 'innova-hybrid-7+1-luxury-plus', name: 'INNOVA HYBRID 7+1', category: 'LUXURY PLUS' },
  { value: 'fortuner-7+1-super-luxury', name: 'FORTUNER 7+1', category: 'SUPER LUXURY' },
  { value: 'tempo-traveller-premium', name: 'TEMPO TRAVELLER', category: 'PREMIUM' },
  { value: 'urbania-luxury', name: 'URBANIA', category: 'LUXURY' },
  { value: 'bus-luxury', name: 'BUS', category: 'LUXURY' },
  { value: 'innova-crysta-6+1-luxury', name: 'INNOVA CRYSTA 6+1', category: 'LUXURY' },
  { value: 'hycross-hybrid-7+1-luxury', name: 'HYCROSS HYBRID 7+1', category: 'LUXURY' },
  { value: 'tempo-traveller-12+1-luxury', name: 'TEMPO TRAVELLER 12+1', category: 'LUXURY' },
  { value: 'tempo-traveller-13+1-luxury', name: 'TEMPO TRAVELLER 13+1', category: 'LUXURY' },
  { value: 'tempo-traveller-7-9-12-luxury', name: 'TEMPO TRAVELLER 7/9/12', category: 'LUXURY' },
  { value: 'urbania-pkn-9+1-luxury', name: 'URBANIA PKN 9+1', category: 'LUXURY' },
];

const buildVehicleOptionValue = (vehicle = {}) => {
  if (vehicle.value) return String(vehicle.value);

  const source = vehicle.apiVehicle || vehicle.rawVehicle || vehicle.sourceVehicle || vehicle.vehicle || vehicle;
  const name = source.vehicleType || vehicle.vehicleType || vehicle.name || vehicle.type || '';
  const category = source.vehicleCategory || vehicle.vehicleCategory || vehicle.category || '';

  return normalizeMatchText([name, category].filter(Boolean).join(' ')).replace(/\s+/g, '-');
};

const buildVehicleSelectOption = (vehicle = {}) => {
  const source = vehicle.apiVehicle || vehicle.rawVehicle || vehicle.sourceVehicle || vehicle.vehicle || vehicle;
  const name = source.vehicleType || vehicle.vehicleType || vehicle.name || vehicle.type || '';
  const category = source.vehicleCategory || vehicle.vehicleCategory || vehicle.category || '';

  if (!name) return null;

  return {
    ...vehicle,
    value: buildVehicleOptionValue(vehicle),
    name,
    category,
    vehicleType: name,
    vehicleCategory: category,
  };
};

const buildVehicleSelectOptions = (vehicles = []) => {
  const groupedOptions = new Map();

  vehicles.forEach((vehicle) => {
    const option = buildVehicleSelectOption(vehicle);
    if (!option) return;

    const key = `${normalizeApiText(option.name)}|${normalizeApiText(option.category)}`;
    const existing = groupedOptions.get(key);

    if (!existing || (!toVehicleBackendId(existing) && toVehicleBackendId(option))) {
      groupedOptions.set(key, option);
    }
  });

  return Array.from(groupedOptions.values());
};

const getExpectedVehicleSpec = (vehicle = {}) => {
  const text = getVehicleMatchText(vehicle);
  return EXPECTED_VEHICLE_SPECS.find((spec) =>
    spec.keywords.some((keyword) => text.includes(normalizeMatchText(keyword)))
  ) || null;
};

const matchesVehicleSpec = (vehicle = {}, spec) => {
  if (!spec) return false;

  const source = vehicle.apiVehicle || vehicle.rawVehicle || vehicle.sourceVehicle || vehicle.vehicle || vehicle;
  return (
    normalizeApiText(source.vehicleType || vehicle.vehicleType || vehicle.name) === spec.vehicleType &&
    normalizeApiText(source.vehicleCategory || vehicle.vehicleCategory || vehicle.category || vehicle.type) === spec.vehicleCategory
  );
};

const MODEL_MATCH_GROUPS = [
  ['ertiga', 'rumion'],
  ['urbania'],
  ['tempo', 'traveller'],
  ['hycross'],
  ['crysta'],
  ['fortuner'],
  ['carens'],
  ['sedan', 'dzire', 'etios', 'ciaz', 'amaze'],
  ['bus'],
];

const GENERIC_MATCH_TOKENS = new Set([
  'taxi',
  'cab',
  'airport',
  'local',
  'outstation',
  'transfer',
  'pickup',
  'drop',
  'dropoff',
  'family',
  'premium',
  'service',
  'extra',
  'luggage',
  'comfort',
  'manual',
  'automatic',
  'diesel',
  'petrol',
  'seater',
  'seaters',
  'seat',
  'seats',
]);

const getRequiredModelGroup = (text = '') =>
  MODEL_MATCH_GROUPS.find((group) => group.some((token) => text.includes(token))) || null;

const scoreVehicleMatch = (candidate = {}, target = {}) => {
  const candidateText = getVehicleMatchText(candidate);
  const targetText = getVehicleMatchText(target);

  if (!candidateText || !targetText) return 0;

  const requiredModelGroup = getRequiredModelGroup(targetText);

  if (requiredModelGroup && !requiredModelGroup.some((token) => candidateText.includes(token))) {
    return 0;
  }

  const targetTokens = new Set(
    targetText
      .split(' ')
      .filter((token) => token.length > 2 && !GENERIC_MATCH_TOKENS.has(token))
  );
  let score = requiredModelGroup ? 100 : 0;

  targetTokens.forEach((token) => {
    if (candidateText.includes(token)) {
      score += token.length > 4 ? 3 : 1;
    }
  });

  const targetSeats = String(target.seats || target.seaters || target.capacity || '').match(/\d+/)?.[0];
  const candidateSeats = String(candidate.seats || candidate.seaters || candidate.capacity || '').match(/\d+/)?.[0];

  if (targetSeats && candidateSeats && targetSeats === candidateSeats) {
    score += 2;
  }

  return score;
};

const findMatchingApiVehicle = (vehicles = [], targetVehicle = {}) =>
  {
    const vehiclesWithBackendId = vehicles.filter((vehicle) => toVehicleBackendId(vehicle) !== null);
    const expectedSpec = getExpectedVehicleSpec(targetVehicle);
    const exactMatch = expectedSpec
      ? vehiclesWithBackendId.find((vehicle) => matchesVehicleSpec(vehicle, expectedSpec))
      : null;

    if (exactMatch) {
      return { vehicle: exactMatch, score: 1000 };
    }

    return vehiclesWithBackendId
      .map((vehicle) => ({ vehicle, score: scoreVehicleMatch(vehicle, targetVehicle) }))
      .sort((first, second) => second.score - first.score)[0];
  };

const resolveVehicleFromApi = async (targetVehicle = {}, bookingType = '') => {
  const apiVehicles = await fetchPublicVehicles();
  const expectedSpec = getExpectedVehicleSpec(targetVehicle);

  if (expectedSpec) {
    const exactRawMatch = apiVehicles.find((vehicle) =>
      toVehicleBackendId(vehicle) !== null && matchesVehicleSpec(vehicle, expectedSpec)
    );

    if (exactRawMatch) {
      return exactRawMatch;
    }
  }

  const vehiclesByTrip = groupVehiclesByTrip(apiVehicles);
  const tripVehicles = vehiclesByTrip[getTripKey(bookingType)] || [];
  const match = findMatchingApiVehicle(tripVehicles, targetVehicle);

  return match?.score > 0 ? match.vehicle : null;
};

const buildSelectedVehicleFallback = (selectedVehicle) => (
  selectedVehicle
    ? {
        id: selectedVehicle,
        name: selectedVehicle,
        type: selectedVehicle,
        category: selectedVehicle,
      }
    : null
);

const mergeVehicleDetails = (...vehicles) => {
  // Filter out null/undefined vehicles
  const validVehicles = vehicles.filter(v => v != null);
  
  if (validVehicles.length === 0) {
    return null;
  }

  const merged = validVehicles.reduce((result, vehicle) => {
    // Skip if vehicle is null/undefined
    if (!vehicle) return result;

    const vehicleBackendId = toVehicleBackendId(vehicle);

    return {
      ...result,
      ...vehicle,
      apiVehicle: result.apiVehicle || vehicle.apiVehicle,
      rawVehicle: result.rawVehicle || vehicle.rawVehicle,
      sourceVehicle: result.sourceVehicle || vehicle.sourceVehicle,
      vehicle: result.vehicle || vehicle.vehicle,
      id: vehicle.id || result.id || vehicleBackendId,
      vehicleId: vehicle.vehicleId || result.vehicleId || vehicleBackendId,
      backendVehicleId: vehicle.backendVehicleId || result.backendVehicleId || vehicleBackendId,
    };
  }, {});

  // If merged is empty or doesn't have any keys, return null
  if (!merged || Object.keys(merged).length === 0) {
    return null;
  }

  const backendId = toVehicleBackendId(merged);

  if (!backendId) return Object.keys(merged).length ? merged : null;

  return {
    ...merged,
    vehicleId: backendId,
    backendVehicleId: backendId,
  };
};

const getVehicleSelectionValue = (vehicle) =>
  buildVehicleOptionValue(vehicle) || vehicle?.id || vehicle?.name || '';

const getFirstVehicleBackendId = (...vehicles) => {
  for (const vehicle of vehicles) {
    // Skip null/undefined vehicles
    if (!vehicle) continue;
    const vehicleId = toVehicleBackendId(vehicle);

    if (vehicleId !== null) {
      return vehicleId;
    }
  }

  return null;
};

const cleanValue = (value) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.length ? value : undefined;
  }

  if (typeof value === 'object') {
    const cleaned = Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, cleanValue(item)])
        .filter(([, item]) => item !== undefined)
    );

    return Object.keys(cleaned).length ? cleaned : undefined;
  }

  return value;
};

const MIN_MESSAGE_LENGTH = 10;

const toOffsetDateTime = (date, time) => {
  if (!date || !time) return undefined;

  const localDateTime = new Date(`${date}T${time}:00`);
  return Number.isNaN(localDateTime.getTime()) ? undefined : localDateTime.toISOString();
};

const buildFormVehicleDetails = (vehicle = {}, selectedVehicleId, selectedRateCard) => {
  const source = vehicle?.apiVehicle || vehicle?.rawVehicle || vehicle?.sourceVehicle || vehicle?.vehicle || vehicle || {};
  const backendId = selectedVehicleId || toVehicleBackendId(vehicle) || toVehicleBackendId(source);

  const vehicleDetails = cleanValue({
    capacity: source.capacity || vehicle.capacity || vehicle.seats || vehicle.seaters,
    fuelType: source.fuelType || vehicle.fuelType || vehicle.fuel,
    // id: backendId ?? null, // COMMENTED OUT - Remove id from payload
    // backendVehicleId: backendId ?? null, // COMMENTED OUT - Remove backendVehicleId from payload
    image: vehicle.image,
    name: source.vehicleType || vehicle.vehicleType || vehicle.name,
    price: vehicle.price,
    registrationNumber: source.registrationNumber || vehicle.registrationNumber,
    seats: source.capacity || vehicle.seats || vehicle.seaters || vehicle.capacity,
    seaters: source.capacity || vehicle.seaters || vehicle.seats || vehicle.capacity,
    status: source.status ?? vehicle.status,
    vehicleCategory: source.vehicleCategory || vehicle.vehicleCategory || vehicle.category,
    vehicleType: source.vehicleType || vehicle.vehicleType || vehicle.name,
    year: source.year || vehicle.year,
  }) || {};

  return vehicleDetails;
};

const BookingFormPopup = ({ isOpen, onClose, onSuccess, onError, selectedVehicleData, tripType, initialRouteData, renderMode = 'modal' }) => {
  // Set booking type to empty string by default (no default selection)
  const [bookingType, setBookingType] = useState('');
  const [airportTripType, setAirportTripType] = useState('cityToAirport');
  const [outstationTripType, setOutstationTripType] = useState('oneWay');
  const [localPackageType, setLocalPackageType] = useState('4hr');
  
  // Form fields
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [pickupPoint, setPickupPoint] = useState('');
  const [dropoffPoint, setDropoffPoint] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [stops, setStops] = useState([]);
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [stopPlaces, setStopPlaces] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [vehicleDetails, setVehicleDetails] = useState(selectedVehicleData || null);
  const [apiVehicleOptions, setApiVehicleOptions] = useState([]);
  
  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Local package details
  const localPackages = {
    '4hr': {
      label: '4 Hours / 40 Kms',
      hours: 4,
      kms: 40,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹1,300/-'
    },
    '8hr': {
      label: '8 Hours / 80 Kms',
      hours: 8,
      kms: 80,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹2,200/-'
    },
    '12hr': {
      label: '12 Hours / 120 Kms',
      hours: 12,
      kms: 120,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹3,200/-'
    }
  };

  const vehicleOptions = useMemo(
    () => (apiVehicleOptions.length ? apiVehicleOptions : BOOKING_VEHICLE_OPTIONS),
    [apiVehicleOptions]
  );

  // Update booking type when tripType prop changes (but only if tripType is provided)
  useEffect(() => {
    if (tripType) {
      setBookingType(normalizeBookingType(tripType));
    }
  }, [tripType]);

  useEffect(() => {
    let isMounted = true;

    const loadVehicleOptions = async () => {
      if (!bookingType) {
        setApiVehicleOptions([]);
        return;
      }

      try {
        const apiVehicles = await fetchPublicVehicles();
        if (!isMounted) return;

        const vehiclesByTrip = groupVehiclesByTrip(apiVehicles);
        const tripVehicles = vehiclesByTrip[getTripKey(bookingType)] || [];
        setApiVehicleOptions(buildVehicleSelectOptions(tripVehicles));
      } catch (error) {
        if (isMounted) {
          setApiVehicleOptions([]);
        }
        console.warn('Unable to load booking vehicle options:', error);
      }
    };

    loadVehicleOptions();

    return () => {
      isMounted = false;
    };
  }, [bookingType]);

  useEffect(() => {
    if (!selectedVehicle || !vehicleDetails || !vehicleOptions.length) return;

    const hasSelectedOption = vehicleOptions.some(
      (option) => String(option.value) === String(selectedVehicle)
    );

    if (hasSelectedOption) return;

    const matchingOption = findMatchingApiVehicle(vehicleOptions, vehicleDetails)?.vehicle;

    if (matchingOption) {
      setSelectedVehicle(matchingOption.value);
      setVehicleDetails(mergeVehicleDetails(vehicleDetails, matchingOption));
    }
  }, [selectedVehicle, vehicleDetails, vehicleOptions]);

  // Set vehicle from selectedVehicleData
  useEffect(() => {
    const resolvedVehicle = mergeVehicleDetails(initialRouteData?.vehicle, selectedVehicleData);

    if (resolvedVehicle) {
      setSelectedVehicle(getVehicleSelectionValue(resolvedVehicle));
      setVehicleDetails(resolvedVehicle);
      
      // If local trip and package info exists, set the package
      if (normalizeBookingType(tripType) === 'Local Taxi' && resolvedVehicle.selectedPackage) {
        setLocalPackageType(resolvedVehicle.selectedPackage);
      }
    }
  }, [selectedVehicleData, initialRouteData?.vehicle, tripType]);

  useEffect(() => {
    if (!initialRouteData) return;

    if (initialRouteData.route) {
      setPickupPoint(initialRouteData.route.pickup || '');
      setDropoffPoint(initialRouteData.route.dropoff || '');
      setPickupPlace(null);
      setDropoffPlace(null);
      setAirportTripType(
        initialRouteData.route.tripDirection === 'Airport to City'
          ? 'airportToCity'
          : 'cityToAirport'
      );
    }

    if (initialRouteData.vehicle || selectedVehicleData) {
      const resolvedVehicle = mergeVehicleDetails(initialRouteData.vehicle, selectedVehicleData);
      setSelectedVehicle(getVehicleSelectionValue(resolvedVehicle));
      setVehicleDetails(resolvedVehicle);
    }
  }, [initialRouteData, selectedVehicleData]);

  useEffect(() => {
    let isMounted = true;

    const hydrateVehicleBackendId = async () => {
      const resolvedVehicle =
        mergeVehicleDetails(initialRouteData?.vehicle, selectedVehicleData, vehicleDetails) ||
        buildSelectedVehicleFallback(selectedVehicle);

      // Add null check here
      if (!resolvedVehicle) {
        return;
      }

      if (toVehicleBackendId(resolvedVehicle) !== null || !bookingType) {
        return;
      }

      try {
        const matchedVehicle = await resolveVehicleFromApi(resolvedVehicle, bookingType);

        if (!isMounted || !matchedVehicle) {
          return;
        }

        const matchedVehicleId = toVehicleBackendId(matchedVehicle);
        // Add null check here
        if (matchedVehicleId === null) {
          return;
        }

        const hydratedVehicle = {
          ...resolvedVehicle,
          vehicleId: matchedVehicleId,
          backendVehicleId: matchedVehicleId,
        };
        if (!vehicleOptions.some((option) => String(option.value) === String(selectedVehicle))) {
          setSelectedVehicle(getVehicleSelectionValue(hydratedVehicle));
        }
        setVehicleDetails(hydratedVehicle);
      } catch (error) {
        console.warn('Unable to hydrate selected vehicle backend ID:', error);
      }
    };

    hydrateVehicleBackendId();

    return () => {
      isMounted = false;
    };
  }, [bookingType, initialRouteData?.vehicle, selectedVehicleData, selectedVehicle, vehicleDetails, vehicleOptions]);

  const handleBookingTypeChange = (value) => {
    const nextBookingType = normalizeBookingType(value);
    setBookingType(nextBookingType);
    setPickupPoint('');
    setDropoffPoint('');
    setStops([]);
    setPickupPlace(null);
    setDropoffPlace(null);
    setStopPlaces([]);
    if (!selectedVehicleData && !initialRouteData?.vehicle) {
      setSelectedVehicle('');
      setVehicleDetails(null);
    }
    setPickupDate('');
    setPickupTime('');
    setDropoffDate('');
    
    // Reset specific states based on booking type
    if (nextBookingType === 'Airport Taxi') {
      setAirportTripType('cityToAirport');
    } else if (nextBookingType === 'Outstation') {
      setOutstationTripType('oneWay');
      setDropoffDate('');
    } else if (nextBookingType === 'Local Taxi') {
      setLocalPackageType('4hr');
    }
  };

  const handleVehicleChange = (value) => {
    const option = vehicleOptions.find((vehicleOption) => String(vehicleOption.value) === String(value));
    const optionBackendId = toVehicleBackendId(option);

    setSelectedVehicle(value);
    setVehicleDetails(
      option
        ? buildFormVehicleDetails(
            {
              ...option,
              id: optionBackendId || option.id || option.value,
              name: option.name,
              type: option.name,
              category: option.category,
              vehicleType: option.name,
              vehicleCategory: option.category,
            },
            optionBackendId
          )
        : null
    );
  };

  // Handle airport terminal selection
  const handleAirportTerminalChange = (value, type) => {
    const airportLocation = value;
    
    if (type === 'pickup') {
      setPickupPoint(airportLocation);
      setPickupPlace(null);
    } else if (type === 'dropoff') {
      setDropoffPoint(airportLocation);
      setDropoffPlace(null);
    }
  };

  const handleDropoffPointChange = (value) => {
    setDropoffPoint(value);
    setDropoffPlace(null);
  };

  const handlePickupPointChange = (value) => {
    setPickupPoint(value);
    setPickupPlace(null);
  };

  // Clear fields when airport trip type changes
  useEffect(() => {
    if (bookingType === 'Airport Taxi' && !initialRouteData?.route) {
      setPickupPoint('');
      setDropoffPoint('');
      setPickupPlace(null);
      setDropoffPlace(null);
    }
  }, [airportTripType, bookingType, initialRouteData]);

  useEffect(() => {
    if (bookingType !== 'Outstation' || outstationTripType !== 'roundTrip') {
      setStops([]);
      setStopPlaces([]);
    }
  }, [bookingType, outstationTripType]);

  const handleAddStop = () => {
    setStops((currentStops) => [...currentStops, '']);
    setStopPlaces((currentStopPlaces) => [...currentStopPlaces, null]);
  };

  const handleStopChange = (index, value) => {
    setStops((currentStops) =>
      currentStops.map((stop, stopIndex) => (stopIndex === index ? value : stop))
    );
    setStopPlaces((currentStopPlaces) =>
      currentStopPlaces.map((stopPlace, stopIndex) => (stopIndex === index ? null : stopPlace))
    );
  };

  const handleRemoveStop = (index) => {
    setStops((currentStops) => currentStops.filter((_, stopIndex) => stopIndex !== index));
    setStopPlaces((currentStopPlaces) => currentStopPlaces.filter((_, stopIndex) => stopIndex !== index));
  };

  const handleStopPlaceSelect = (index, place) => {
    setStops((currentStops) =>
      currentStops.map((stop, stopIndex) => (stopIndex === index ? place.address : stop))
    );
    setStopPlaces((currentStopPlaces) =>
      currentStopPlaces.map((stopPlace, stopIndex) => (stopIndex === index ? place : stopPlace))
    );
  };

  const getBackendTripType = () => {
    if (bookingType === 'Airport Taxi') {
      return airportTripType === 'airportToCity' ? 'Airport PickUp' : 'Airport Drop';
    }

    if (bookingType === 'Outstation') return 'outstation';
    if (bookingType === 'Local Taxi') return 'local';

    return undefined;
  };

  const resolveVehicleDetailsForSubmit = async () => {
    // Try to merge all vehicle sources
    let currentVehicle = mergeVehicleDetails(initialRouteData?.vehicle, selectedVehicleData, vehicleDetails);
    
    // If merge returned null, try the fallback
    if (!currentVehicle) {
      currentVehicle = buildSelectedVehicleFallback(selectedVehicle);
    }
    
    // If still null, return null early - DO NOT call toVehicleBackendId with null
    if (!currentVehicle) {
      console.warn('No vehicle found for submission');
      return null;
    }

    // Now it's safe to call toVehicleBackendId since currentVehicle is not null
    const backendId = toVehicleBackendId(currentVehicle);
    if (backendId !== null) {
      return currentVehicle;
    }

    // Try to resolve from API
    const matchedVehicle = await resolveVehicleFromApi(currentVehicle, bookingType);
    
    // Check if matchedVehicle is null before calling toVehicleBackendId
    if (!matchedVehicle) {
      return currentVehicle;
    }
    
    const matchedVehicleId = toVehicleBackendId(matchedVehicle);

    if (matchedVehicleId === null) {
      return currentVehicle;
    }

    const resolvedVehicle = {
      ...currentVehicle,
      vehicleId: matchedVehicleId,
      backendVehicleId: matchedVehicleId,
    };

    if (!vehicleOptions.some((option) => String(option.value) === String(selectedVehicle))) {
      setSelectedVehicle(getVehicleSelectionValue(resolvedVehicle));
    }
    setVehicleDetails(resolvedVehicle);

    return resolvedVehicle;
  };

  const prepareWebsiteEnquiryPayload = async () => {
    const resolvedVehicleDetails = await resolveVehicleDetailsForSubmit();
    
    // If no vehicle details, throw error
    if (!resolvedVehicleDetails) {
      throw new Error('No vehicle selected. Please select a vehicle to continue.');
    }
    
    const rateCardTripType = isAirport ? getAirportRateCardTripType(airportTripType) : undefined;
    const vehiclePayload = buildSelectedVehiclePayloadContext({
      vehicle: resolvedVehicleDetails,
      initialRouteData,
      bookingType,
      rateCardTripType,
    });
    const selectedPackage = vehiclePayload.rateCard.packageDetails || resolvedVehicleDetails?.packageDetails || localPackages[localPackageType];
    const routeDetails = initialRouteData?.route || {};
    const pricingDetails = initialRouteData?.pricing || {};
    const enteredStops = stops.map((stop) => stop.trim()).filter(Boolean);
    const usageRoute = isOutstation
      ? [
          pickupPoint.trim(),
          ...enteredStops,
          dropoffPoint.trim(),
          outstationTripType === 'roundTrip' ? pickupPoint.trim() : '',
        ].filter(Boolean).join('-')
      : undefined;
    const selectedVehicleId = getFirstVehicleBackendId(
      vehiclePayload.vehicleDetails,
      resolvedVehicleDetails,
      vehicleDetails,
      selectedVehicleData,
      initialRouteData?.vehicle
    );
    const selectedVehicleLongId = Number(selectedVehicleId);
    const selectedRateCard = vehiclePayload.rateCard.selectedRateCard || vehiclePayload.rateCard;
    const submittedVehicle = buildFormVehicleDetails(resolvedVehicleDetails, selectedVehicleId, selectedRateCard);
    const selectedVehicleName = resolvedVehicleDetails?.name || selectedVehicle || undefined;
    const selectedVehicleCategory = resolvedVehicleDetails?.category || resolvedVehicleDetails?.type || bookingType;
    const expectedTravelDate = toOffsetDateTime(pickupDate, pickupTime);
    const selectedVehicleTripFields = {
      pickupTime: expectedTravelDate,
      ...(isOutstation ? { outstationTripType } : {}),
      ...(usageRoute ? { usage: usageRoute } : {}),
      ...(isOutstation && dropoffDate ? { returnDate: dropoffDate } : {}),
      ...(selectedVehicleName ? { selectedVehicleName, vehiclePreference: selectedVehicleName } : {}),
      ...(selectedVehicleCategory ? { selectedVehicleCategory } : {}),
    };
    
    // Handle null/undefined selectedVehicles and remove id, backendVehicleId, vehicleId
    const selectedVehiclesArray = vehiclePayload.selectedVehicles || [];
    const submittedSelectedVehicles = selectedVehiclesArray.length
      ? selectedVehiclesArray.map((vehicle) => {
          // Remove id, backendVehicleId, and vehicleId from vehicle object
          const { id, backendVehicleId, vehicleId, ...vehicleWithoutIds } = vehicle || {};
          return {
            ...vehicleWithoutIds,
            ...selectedVehicleTripFields,
          };
        })
      : Object.keys(submittedVehicle).length ? [{ ...submittedVehicle, selectedRateCard, ...selectedVehicleTripFields }] : [];
    
    const selectedVehicleTypes = [
      selectedVehicleCategory,
      selectedVehicleName,
      selectedVehicle,
      bookingType,
    ].filter(Boolean);
    const backendTripType = getBackendTripType();

    return buildWebsiteEnquiryPayload({
      customerName: name,
      customerEmail: email,
      customerPhone: mobile,
      message,
      expectedTravelDate,
      expectedPax: vehiclePayload.expectedPax || resolvedVehicleDetails?.seats || resolvedVehicleDetails?.seaters || resolvedVehicleDetails?.capacity || undefined,
      expectedBudget: vehiclePayload.expectedBudget || String(routeDetails.price || pricingDetails.basePrice || resolvedVehicleDetails?.price || '').replace(/[^0-9.]/g, '') || undefined,
      selectedVehicleIds: Number.isInteger(selectedVehicleLongId) && selectedVehicleLongId > 0
        ? [selectedVehicleLongId]
        : vehiclePayload.selectedVehicleIds,
      selectedVehicleTypes: vehiclePayload.selectedVehicleTypes.length ? vehiclePayload.selectedVehicleTypes : selectedVehicleTypes,
      preferredVehicleCategory: bookingType === 'Innova Taxi' ? 'LUXURY' : 'STANDARD',
      pickupLocation: pickupPoint,
      dropLocation: dropoffPoint || pickupPoint,
      tripType: backendTripType,
      formData: {
        selectedVehicles: submittedSelectedVehicles,
        rateCardTripType,
        airportTripType: isAirport ? airportTripType : undefined,
        localPackage: isLocal ? selectedPackage?.label || undefined : undefined,
      },
    });
  };

  const validateForm = () => {
    if (!bookingType) return 'Please select a booking type';
    if (!name.trim()) return 'Name is required';
    if (!mobile.trim()) return 'Mobile number is required';
    if (mobile.trim().length !== 10) return 'Mobile number must be 10 digits';
    if (!/^\d{10}$/.test(mobile.trim())) return 'Mobile number must contain only digits';
    if (!email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(email.trim())) return 'Please enter a valid email address';
    if (message.trim() && message.trim().length < MIN_MESSAGE_LENGTH) return `Message must be at least ${MIN_MESSAGE_LENGTH} characters`;
    
    if (!pickupPoint.trim()) return 'Pickup point is required';
    
    if (!isLocal && !dropoffPoint.trim()) return 'Dropoff point is required';

    const needsGooglePickup = !(isAirport && airportTripType === 'airportToCity');
    const needsGoogleDropoff = !isLocal && !(isAirport && airportTripType === 'cityToAirport');

    if ((needsGooglePickup && !pickupPlace) || (needsGoogleDropoff && !dropoffPlace)) {
      return 'Please select pickup and drop locations from the Google suggestions.';
    }

    if (isOutstation && outstationTripType === 'roundTrip') {
      const hasUnselectedStop = stops.some((stop, index) => stop.trim() && !stopPlaces[index]);
      if (hasUnselectedStop) {
        return 'Please select each stop location from the Google suggestions.';
      }
    }
    
    if (!pickupDate) return 'Pickup date is required';
    if (!pickupTime) return 'Pickup time is required';
    
    if (isOutstation && outstationTripType === 'roundTrip' && !dropoffDate) {
      return 'Return date is required for round trips';
    }
    
    if (!isLocal && !selectedVehicle) return 'Please select a vehicle';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setSubmitError(validationError);
      if (onError) onError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = await prepareWebsiteEnquiryPayload();
      console.log('📤 Sending booking payload:', payload);
      
      // Call the booking service
      const response = await bookingService.submitWebsiteEnquiry(payload);
      
      console.log('✅ Booking successful:', response);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(response);
      }

      if (renderMode === 'page') {
        return;
      }
      
      // Show success message
      alert(`✅ Booking confirmed!\nBooking ID: ${response.bookingId || response.id || response.bookingReference}\nWe'll contact you shortly on ${mobile}.`);
      
      // Close the popup
      onClose();
      
    } catch (error) {
      console.error('❌ Booking failed:', error);
      
      const errorMessage = error.message || 'Failed to create booking. Please try again.';
      setSubmitError(errorMessage);
      
      // Call error callback
      if (onError) {
        onError(errorMessage);
      }
      
      alert(`❌ Booking failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form after close
    setTimeout(() => {
      setBookingType('');
      setAirportTripType('cityToAirport');
      setOutstationTripType('oneWay');
      setLocalPackageType('4hr');
      setName('');
      setMobile('');
      setEmail('');
      setMessage('');
      setPickupPoint('');
      setDropoffPoint('');
      setPickupPlace(null);
      setDropoffPlace(null);
      setPickupDate('');
      setPickupTime('');
      setDropoffDate('');
      setStops([]);
      setStopPlaces([]);
      setSelectedVehicle('');
      setVehicleDetails(null);
      setSubmitError('');
    }, 300);
  };

  if (!isOpen) return null;

  const isAirport = bookingType === 'Airport Taxi';
  const isOutstation = bookingType === 'Outstation';
  const isLocal = bookingType === 'Local Taxi';
  const isOutstationRoundTrip = isOutstation && outstationTripType === 'roundTrip';
  const showVehicleSelection = bookingType !== 'Local Taxi';
  const isPageMode = renderMode === 'page';

  // Helper to render airport terminal select
  const renderAirportTerminalSelect = (type, value, onChange) => {
    const terminalOptions = [
      { value: 'KIA Airport Bangalore - Terminal 1', label: 'KIA Airport Bangalore - Terminal 1' },
      { value: 'KIA Airport Bangalore - Terminal 2', label: 'KIA Airport Bangalore - Terminal 2' },
    ];

    return (
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          handleAirportTerminalChange(e.target.value, type);
        }}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        disabled={isSubmitting}
        required
      >
        <option value="">Select airport terminal</option>
        {terminalOptions.map((terminal) => (
          <option key={terminal.value} value={terminal.value}>
            {terminal.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div
      className={isPageMode ? 'w-full max-w-3xl mx-auto' : 'fixed inset-0 z-50 overflow-y-auto'}
      aria-labelledby="modal-title"
      role={isPageMode ? undefined : 'dialog'}
      aria-modal={isPageMode ? undefined : 'true'}
    >
      <div className={isPageMode ? 'px-0 text-left' : 'flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'}>
        {/* Background overlay */}
        {!isPageMode && (
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
            onClick={handleClose}
          ></div>
        )}

        {/* Center modal */}
        {!isPageMode && <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>}
        
        <div className={isPageMode ? 'bg-white rounded-2xl text-left overflow-hidden shadow-xl w-full' : 'inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full'}>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900" id="modal-title">
                    Book Your Ride
                  </h3>
                  {!isPageMode && (
                    <button
                      onClick={handleClose}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <span className="text-2xl">&times;</span>
                    </button>
                  )}
                </div>

                {/* Error message display */}
                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Personal Information */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your name"
                        disabled={isSubmitting}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mobile *</label>
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                          +91
                        </span>
                        <input
                          type="tel"
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                          className="flex-1 block w-full border border-gray-300 rounded-none rounded-r-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Mobile number"
                          pattern="[0-9]{10}"
                          maxLength="10"
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="your@email.com"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Booking Type - No default selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Booking Type *</label>
                    <select
                      value={bookingType}
                      onChange={(e) => handleBookingTypeChange(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select booking type</option>
                      <option value="Airport Taxi">Airport Taxi</option>
                      <option value="Outstation">Outstation</option>
                      <option value="Local Taxi">Local Taxi</option>
                    </select>
                  </div>

                  {/* Show fields only after booking type is selected */}
                  {bookingType && (
                    <>
                      {/* Airport Trip Type - Shows only for Airport Taxi */}
                      {isAirport && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Trip Direction *</label>
                          <div className="flex gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="airportTripType"
                                checked={airportTripType === 'cityToAirport'}
                                onChange={() => setAirportTripType('cityToAirport')}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={isSubmitting}
                              />
                              <span className="ml-2 text-sm text-gray-700">City to Airport</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="airportTripType"
                                checked={airportTripType === 'airportToCity'}
                                onChange={() => setAirportTripType('airportToCity')}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={isSubmitting}
                              />
                              <span className="ml-2 text-sm text-gray-700">Airport to City</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Outstation Trip Type */}
                      {isOutstation && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Trip Type *</label>
                          <div className="flex gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="outstationTripType"
                                checked={outstationTripType === 'roundTrip'}
                                onChange={() => setOutstationTripType('roundTrip')}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={isSubmitting}
                              />
                              <span className="ml-2 text-sm text-gray-700">Round Trip</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="outstationTripType"
                                checked={outstationTripType === 'oneWay'}
                                onChange={() => setOutstationTripType('oneWay')}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={isSubmitting}
                              />
                              <span className="ml-2 text-sm text-gray-700">One Way</span>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Local Package Selection */}
                      {isLocal && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Select Package *</label>
                          <select
                            value={localPackageType}
                            onChange={(e) => setLocalPackageType(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={isSubmitting}
                            required
                          >
                            <option value="4hr">4 Hours / 40 Kms</option>
                            <option value="8hr">8 Hours / 80 Kms</option>
                            <option value="12hr">12 Hours / 120 Kms</option>
                          </select>
                        </div>
                      )}

                      {/* Vehicle Selection - not shown for Local Taxi */}
                      {showVehicleSelection && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Select Vehicle *</label>
                          <select
                            value={selectedVehicle}
                            onChange={(e) => handleVehicleChange(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={isSubmitting}
                            required
                          >
                            <option value="">Select a vehicle</option>
                            {vehicleOptions.map((vehicleOption) => (
                              <option key={vehicleOption.value} value={vehicleOption.value}>
                                {vehicleOption.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Pickup and Dropoff Points */}
                      <div className="space-y-3">
                        {/* Pickup Point */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Pickup Point *
                            {isAirport && airportTripType === 'airportToCity' && (
                              <span className="ml-2 text-xs text-blue-600">(Airport pickup)</span>
                            )}
                          </label>
                          
                          {isAirport && airportTripType === 'airportToCity' ? (
                            renderAirportTerminalSelect('pickup', pickupPoint, (value) => {
                              // This will be handled by the select's onChange
                            })
                          ) : (
                            <LocationAutocompleteInput
                              value={pickupPoint}
                              onChange={handlePickupPointChange}
                              onPlaceSelect={(place) => {
                                setPickupPlace(place);
                                setPickupPoint(place.address);
                              }}
                              onSelectionClear={() => setPickupPlace(null)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter pickup location"
                              disabled={isSubmitting}
                              required
                            />
                          )}
                        </div>

                        {/* Dropoff Point */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            {isOutstationRoundTrip ? 'Destination' : 'Dropoff Point'} *
                            {isAirport && airportTripType === 'cityToAirport' && (
                              <span className="ml-2 text-xs text-blue-600">(Airport dropoff)</span>
                            )}
                          </label>
                          
                          {isAirport && airportTripType === 'cityToAirport' ? (
                            renderAirportTerminalSelect('dropoff', dropoffPoint, (value) => {
                              // This will be handled by the select's onChange
                            })
                          ) : (
                            <LocationAutocompleteInput
                              value={dropoffPoint}
                              onChange={handleDropoffPointChange}
                              onPlaceSelect={(place) => {
                                setDropoffPlace(place);
                                setDropoffPoint(place.address);
                              }}
                              onSelectionClear={() => setDropoffPlace(null)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter dropoff location"
                              disabled={isSubmitting}
                              required={!isLocal}
                            />
                          )}
                        </div>

                        {/* Stops for Round Trip */}
                        {isOutstation && outstationTripType === 'roundTrip' && (
                          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <label className="block text-sm font-medium text-gray-700">
                                Add Stop
                              </label>
                              <button
                                type="button"
                                onClick={handleAddStop}
                                disabled={isSubmitting}
                                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <Plus className="h-4 w-4" aria-hidden="true" />
                                Add stop
                              </button>
                            </div>

                            {stops.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {stops.map((stop, index) => (
                                  <div key={index}>
                                    <label className="block text-xs font-medium text-gray-600">
                                      Stop {index + 1}
                                    </label>
                                    <div className="mt-1 flex gap-2">
                                      <LocationAutocompleteInput
                                        value={stop}
                                        onChange={(value) => handleStopChange(index, value)}
                                        onPlaceSelect={(place) => handleStopPlaceSelect(index, place)}
                                        onSelectionClear={() => handleStopChange(index, stop)}
                                        className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                                        wrapperStyle={{ flex: 1 }}
                                        placeholder="Enter stop location"
                                        disabled={isSubmitting}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveStop(index)}
                                        disabled={isSubmitting}
                                        aria-label={`Remove stop ${index + 1}`}
                                        className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-md border border-red-200 bg-white text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                      >
                                        <Minus className="h-4 w-4" aria-hidden="true" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Dates and Times */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Pickup Date *</label>
                          <input
                            type="date"
                            value={pickupDate}
                            onChange={(e) => setPickupDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            disabled={isSubmitting}
                            required
                          /> 
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Pickup Time *</label>
                          <select
                            value={pickupTime}
                            onChange={(e) => setPickupTime(e.target.value)}
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.5rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em',
                              paddingRight: '2.5rem'
                            }}
                          >
                            <option value="">Select time</option>
                            {Array.from({ length: 24 * 4 }, (_, i) => {
                              const hour = Math.floor(i / 4);
                              const minute = (i % 4) * 15;
                              const period = hour < 12 ? 'AM' : 'PM';
                              const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                              const displayMinute = minute.toString().padStart(2, '0');
                              const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                              const displayTime = `${displayHour}:${displayMinute} ${period}`;
                              
                              return (
                                <option key={i} value={timeValue}>
                                  {displayTime}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Return Date - only for Outstation Round Trip */}
                        {isOutstation && outstationTripType === 'roundTrip' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Return Date *</label>
                            <input
                              type="date"
                              value={dropoffDate}
                              onChange={(e) => setDropoffDate(e.target.value)}
                              min={pickupDate}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              disabled={isSubmitting}
                              required
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter message"
                      disabled={isSubmitting}
                      minLength={MIN_MESSAGE_LENGTH}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional. Minimum {MIN_MESSAGE_LENGTH} characters if provided.
                    </p>
                  </div>

                  {/* Book Now Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        'Book Now'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFormPopup;